import React, { useState, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Icons } from '../Icons/Icons';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import './ImageUpload.scss';

interface ImageUploadProps {
  error?: string;
  touched?: boolean;
  fieldName?: string;
  existingImageUrl?: string;
  isRequired?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  fieldName = 'thumbnail',
  existingImageUrl,
  isRequired = false,
}) => {
  const [preview, setPreview] = useState<string | null>(existingImageUrl || null);
  const { setFieldValue, setFieldTouched, values, submitCount } = useFormikContext<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { storageUrl } = useStorageUrl();

  useEffect(() => {
    if (existingImageUrl && !preview) {
      setPreview(storageUrl ? `${storageUrl}/${existingImageUrl}` : existingImageUrl);
    }
  }, [existingImageUrl, preview, storageUrl]);

  useEffect(() => {
    const file = values[fieldName];
    if (file && file instanceof File && !preview) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, [values, fieldName, preview]);

  useEffect(() => {
    if (isRequired) {
      setFieldTouched(fieldName, submitCount > 0, false);
    }
  }, [submitCount, fieldName, setFieldTouched, isRequired]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue(fieldName, file);
      setFieldTouched(fieldName, true, true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setFieldValue(fieldName, file);
      setFieldTouched(fieldName, true, true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== fileInputRef.current) {
      fileInputRef.current?.click();
    }
  };

  const hasValidImage = values[fieldName] instanceof File || Boolean(existingImageUrl);
  const isTouched = Boolean(submitCount > 0 || values[fieldName]);
  const showError = isRequired && isTouched && !hasValidImage;
  const errorClass = showError ? 'error' : '';

  return (
    <div className="image-upload-container">
      <div
        className={`image-upload ${errorClass}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleContainerClick}
      >
        {preview ? (
          <div className="image-preview">
            <img src={preview} alt="Preview" />
          </div>
        ) : (
          <>
            <div className="upload-icon">{Icons.Upload}</div>
            <span className="upload-text">
              <strong>Click to upload an image</strong>
              <br /> or drag and drop
              {isRequired && <span className="required">*</span>}
            </span>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleImageChange}
          accept="image/*"
          className="file-input"
        />
      </div>
      {showError && <div className="error">Image is required</div>}
    </div>
  );
};
